import { spaces } from '@kimuno/theme';
import {
	Box,
	Button,
	type ButtonProps,
	CircularProgress,
	DialogActions as MuiDialogActions,
} from '@mui/material';
import type React from 'react';
import { useTranslation } from 'react-i18next';

import useScreenSize from '../../../utils/useScreenSize';

interface DialogActionsButtonProps extends ButtonProps {
	text: string;
	loading?: boolean;
}
interface DialogActionsProps {
	onBack?: () => void;
	primaryButton: DialogActionsButtonProps;
	secondaryButton: DialogActionsButtonProps;
}

const DialogActions: React.FC<DialogActionsProps> = ({
	onBack,
	primaryButton,
	secondaryButton,
}) => {
	const { t } = useTranslation();
	const { isXsScreen, isSmScreen } = useScreenSize();
	const isMobile = isXsScreen || isSmScreen;

	return (
		<MuiDialogActions
			sx={{
				display: 'flex',
				flexDirection: isMobile ? 'column-reverse' : 'row',
				justifyContent: 'space-between',
				alignItems: 'center',
				padding: spaces.lg,
			}}
		>
			{onBack && (
				<Button
					variant="text"
					color="inherit"
					size="large"
					sx={{
						padding: `${spaces.xs} ${spaces.lg}`,
						width: isMobile ? '100%' : 'auto',
					}}
					onClick={onBack}
					type="button"
					aria-label={t('Back')}
				>
					{t('Back')}
				</Button>
			)}
			<Box
				sx={{
					display: 'flex',
					gap: spaces.xs,
					justifyContent: 'flex-end',
					flexDirection: isMobile ? 'column-reverse' : 'row',
					width: '100%',
				}}
			>
				<Button
					onClick={secondaryButton.onClick}
					variant={secondaryButton?.variant || 'outlined'}
					color={secondaryButton?.color || 'primary'}
					disabled={secondaryButton?.disabled}
					size="large"
					sx={{
						padding: `${spaces.xs} ${spaces.lg}`,
						width: isMobile ? '100%' : 'auto',
					}}
					data-testid="dialog-actions-secondary-cta-button"
					type="button"
					aria-label={secondaryButton?.text || t('Cancel')}
				>
					{secondaryButton?.text || t('Cancel')}
				</Button>
				<Box sx={{ display: 'flex', alignItems: 'center' }}>
					<Box sx={{ position: 'relative', width: isMobile ? '100%' : 'auto' }}>
						<Button
							variant={primaryButton?.variant || 'contained'}
							color={primaryButton?.color || 'primary'}
							size="large"
							onClick={primaryButton.onClick}
							disabled={primaryButton?.disabled || primaryButton?.loading}
							sx={{
								padding: `${spaces.xs} ${spaces.lg}`,
								width: 'inherit',
							}}
							data-testid="dialog-actions-primary-cta-button"
							type="button"
							aria-label={primaryButton.text}
							aria-busy={primaryButton?.loading}
						>
							{primaryButton.text}
						</Button>
						{primaryButton?.loading && (
							<CircularProgress
								size={22}
								sx={{
									color: 'white',
									position: 'absolute',
									top: '50%',
									left: '50%',
									transform: 'translate(-50%, -50%)!important',
									'& .MuiCircularProgress-svg': {
										width: 'auto',
										height: 'auto',
									},
								}}
							/>
						)}
					</Box>
				</Box>
			</Box>
		</MuiDialogActions>
	);
};

export default DialogActions;
